'use client';

import { useCallback, useState } from 'react';

import { ImageUploader } from '@ps/ui/upload/components/ImageUploader';
import { uploadFileAsync } from '@ps/ui/upload/functions/uploadFileAsync';
import { UserRound } from 'lucide-react';

/** Matches `ImageUploader`'s own default — declared so the copy can state it. */
const MAX_ICON_FILE_SIZE_MB = 10;

interface Props {
  /** Current icon URL — uploaded image is displayed in the avatar slot. */
  value?: string;
  /**
   * Fires with the new CDN URL on successful upload, or with `undefined`
   * if the consumer should clear the icon (not used today; reserved
   * for a future "remove" affordance).
   */
  onChange?: (url: string | undefined) => void;
  /** Disable interaction (during outer submit, etc). */
  disabled?: boolean;
}

/**
 * The "channel icon upload" row inside Create / Edit Channel modals.
 * Layout matches the .pen design — circular placeholder avatar on
 * the left (just a visual mask; the cropper itself uses a square
 * frame), dashed-border drop zone with "Click to upload channel
 * icon" copy + "Upload" button on the right, with an in-place
 * spinner over the dashed border while a PUT is in flight.
 *
 * Wraps:
 *   - `ImageUploader` (libs/ui) for file pick + 1:1 square crop
 *   - `uploadFileAsync` for the Firebase Storage / Google Cloud
 *     Storage upload (matches PopshopRN's channel-editor, which
 *     uploads icons through the bio ImageUploader → `uploadFile()`
 *     path, not through the media-gallery presigned S3 pipeline)
 *
 * The component owns the upload + value state internally; consumers
 * only need to bind `value` / `onChange`. Errors surface inline as
 * a small error string below the row.
 */
export function ChannelIconUploadRow({ value, onChange, disabled }: Props) {
  const [iconUrl, setIconUrl] = useState<string | undefined>(value);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const handleFile = useCallback(
    async (file: File) => {
      setErrorMessage(undefined);
      setIsUploading(true);

      // Construct the LocalFileInfo shape `uploadFileAsync` expects.
      // The helper exists so it can plug into `FileUploader`'s
      // injected `uploadFile` prop; when called directly the caller
      // owns the wrapping.
      const localUri = URL.createObjectURL(file);
      try {
        const url = await uploadFileAsync({
          source: 'local',
          filename: file.name,
          size: file.size,
          type: file.type,
          localUri,
          localFile: file,
        });
        setIconUrl(url);
        onChange?.(url);
      } catch (uploadError) {
        setErrorMessage(
          uploadError instanceof Error
            ? uploadError.message
            : 'Failed to upload icon.',
        );
      } finally {
        URL.revokeObjectURL(localUri);
        setIsUploading(false);
      }
    },
    [onChange],
  );

  const handlePickerError = useCallback((message: string) => {
    setErrorMessage(message);
  }, []);

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      <ImageUploader
        crop={{ aspectRatio: 1 }}
        maxSizeMB={MAX_ICON_FILE_SIZE_MB}
        onFile={handleFile}
        onError={handlePickerError}
      >
        {({ onClick, isCropping }) => {
          const busy = isCropping || isUploading;
          return (
            <div className="flex items-center gap-3">
              <span className="bg-surface-2 relative flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-full">
                {iconUrl ? (
                  // Channel icons aren't in the `next/image` remote
                  // allowlist — plain <img> matches `ChannelAvatar`'s
                  // `unoptimized` pattern.
                  <img
                    src={iconUrl}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <UserRound className="text-content-4 h-9 w-9" aria-hidden />
                )}
              </span>

              <div className="border-line-strong flex h-[72px] flex-1 items-center justify-between gap-3 rounded-lg border border-dashed px-4">
                <div className="flex flex-1 items-center gap-2 overflow-hidden">
                  {busy ? (
                    <span
                      aria-label="Uploading"
                      className="border-line-strong h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-t-blue-400"
                    />
                  ) : null}
                  <span className="flex min-w-0 flex-col">
                    <span className="text-content-2 text-sm leading-tight">
                      Click to upload channel icon
                    </span>
                    <span className="text-content-3 text-xs">
                      Max {MAX_ICON_FILE_SIZE_MB}MB
                    </span>
                  </span>
                </div>
                <button
                  type="button"
                  onClick={onClick}
                  disabled={disabled || busy}
                  className="shrink-0 rounded-md border border-blue-500 bg-white px-4 py-2 text-sm font-semibold text-blue-500 transition disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Upload
                </button>
              </div>
            </div>
          );
        }}
      </ImageUploader>

      {errorMessage ? (
        <p role="alert" className="text-xs text-red-300">
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
}
